<template>
  <artmisads-modal
    v-model:open="visible"
    :title="st('settingsWhitelist.copyModalTitle')"
    :width="600"
    :maskClosable="false"
    @cancel="handleCancel"
  >
    <div class="copy-modal-content">
      <div class="info-row">
        <span class="info-label">{{ st('settingsWhitelist.copyModalCurrentMarketplace') }}</span>
        <span class="info-value">{{ currentMarketplace }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">{{ st('settingsWhitelist.copyModalPublishersCount') }}</span>
        <span class="info-value">{{ publisherCount }}</span>
      </div>
      <div class="target-section">
        <div class="info-label">{{ st('settingsWhitelist.copyModalSelectTarget') }}</div>
        <div class="checkbox-list">
          <a-checkbox
            v-for="item in targetMarketplaces"
            :key="item.host"
            :checked="selectedCountryCodes.includes(item.host)"
            @change="e => onCheckboxChange(e, item.host)"
          >
            {{ item.host }}
          </a-checkbox>
        </div>
      </div>
      <div class="notice-box">
        <p>{{ st('settingsWhitelist.copyModalNotice1') }}</p>
        <p>{{ st('settingsWhitelist.copyModalNotice2') }}</p>
      </div>
    </div>
    <template #footer>
      <artmisads-button @click="handleCancel">
        {{ st('settingsWhitelist.btnCancel') }}
      </artmisads-button>
      <artmisads-button
        type="primary"
        :disabled="!selectedCountryCodes.length"
        :loading="submitting"
        @click="handleSubmit"
      >
        {{ st('settingsWhitelist.copyModalSubmit') }}
      </artmisads-button>
    </template>
  </artmisads-modal>
</template>

<script setup>
import { ref, computed } from 'vue'
import { message } from 'ant-design-vue'
import { whitelistApi } from '@/server/seller/index'
import { useSellerI18n } from '@/composables/useSellerI18n'
import { useUserInfoStore } from '@/storeData/index'
import { COUNTRY_BASE } from '@/contants/common'

const { st } = useSellerI18n()
const store = useUserInfoStore()

const visible = ref(false)
const submitting = ref(false)
const publisherCount = ref(0)
const selectedCountryCodes = ref([])

const currentCountryCode = computed(() => store.userInfo?.countryCode || 'US')

const currentMarketplace = computed(() => {
  return COUNTRY_BASE.find(item => item.countryCode === currentCountryCode.value)?.host || ''
})

const targetMarketplaces = computed(() =>
  COUNTRY_BASE.filter(item => item.countryCode !== currentCountryCode.value)
)

const onCheckboxChange = (e, countryCode) => {
  if (e.target.checked) {
    selectedCountryCodes.value = [...selectedCountryCodes.value, countryCode]
  } else {
    selectedCountryCodes.value = selectedCountryCodes.value.filter(code => code !== countryCode)
  }
}

const handleSubmit = async () => {
  if (!selectedCountryCodes.value.length) return
  submitting.value = true
  try {
    const res = await whitelistApi.copyWhitelistToMarketplaces({
      marketplaces: selectedCountryCodes.value,
    })
    if (res.success) {
      message.success(st('settingsWhitelist.copySuccess'))
      visible.value = false
    } else {
      message.error(res.error)
    }
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  visible.value = false
}

const open = count => {
  publisherCount.value = count ?? 0
  selectedCountryCodes.value = []
  visible.value = true
}

defineExpose({ open })
</script>

<style lang="less" scoped>
.copy-modal-content {
  .info-row {
    display: flex;
    flex-direction: column;
    
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .info-label {
    font-size: 14px;
    font-weight: 600;
    color: #0f0629;
    letter-spacing: 0.3px;
  }

  .info-value {
    font-size: 14px;
    color: #0f0629;
    letter-spacing: 0.3px;
  }

  .target-section {
    margin-bottom: 16px;
  }

  .checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
    padding: 16px;
    border: 1px solid #e8e5ed;
    border-radius: 8px;
  }

  .notice-box {
    padding: 12px 16px;
    background-color: #f8f7fc;
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.65);
      letter-spacing: 0.3px;

      & + p {
        margin-top: 4px;
      }
    }
  }
}
</style>
